/**
 * /pipeline-facility/digs/:id — one integrity dig (GET / PATCH / DELETE
 * /pipeline-facility/digs/:id and its features, repairs, welds, PT links and
 * report; fieldiq-api#1219, PF-3a).
 *
 * THE STATUS BUTTONS ARE THE API'S `allowedNext`, less `reported` — a dig is
 * reported only by issuing its report, so a backfilled dig points at the
 * report card instead of drawing a "Reported" button. Every move is confirmed
 * inline; a refusal (features unassessed, no repair recorded, …) comes back as
 * a 409 and is said as a sentence in the refusal style where the button was.
 *
 * Remaining WT and its % are the API's, shown as sent; an absent figure is `—`
 * with the API's reason. Nothing here recomputes it.
 *
 * Roles: the sheet, features and repairs are PF_WORK (a field hand must be on
 * the crew — the API decides); planning / deleting the dig, links and report
 * writes are PF_DISPATCH. Forms open inline; there are no modals.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Activity, Hammer, Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfMeta } from '../../../hooks/usePipelineFacility';
import {
  useAddPfFeature,
  useAddPfRepair,
  useCreatePfReport,
  useDeletePfDig,
  useDeletePfFeature,
  useDeletePfRepair,
  useUnlinkPfPt,
  useUpdatePfDig,
  useUpdatePfFeature,
  usePfAnomalies,
  usePfDig,
  usePfReport,
} from '../../../hooks/usePfDigs';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { blankToNull, canDispatchPf, canWorkPf, catalogLabel, fmtNumber, pfDay, toInput, withUnit } from '../../../lib/pipeline-facility';
import {
  numOrNull,
  pfAssessmentSkin,
  pfDigAwaitsReport,
  pfDigEditable,
  pfDigRefusal,
  pfDigStatusSkin,
  pfDigSteps,
  PF_DIG_MOVE_LABEL,
  pfWeldResultSkin,
  remainingWtBasisLabel,
  remainingWtText, coatingText } from '../../../lib/pf-digs';
import type { PfCatalog, PfMeta } from '../../../types/pipeline-facility';
import type { PfDigDetail, PfDigFeature, PfDigPatch, PfDigRepair } from '../../../types/pf-digs';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, PF_DATE_INPUT, PF_EYEBROW, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfBadge, PfEmpty, PfField, PfFormError, PfLoading, PfPanel, PfSpec } from '../PfBits';
import { oneCallText } from './DigsScreen';
import { PfFormGrid, PfInlineConfirm, PfNote, unitLabel } from './DigBits';
import { FeatureForm } from './FeatureForm';
import { PtLinkPicker } from './LinkPickers';
import { ReportPanelBody } from './ReportPanel';

const MOVE_BODY: Record<string, string> = {
  excavated: 'Marks the pipe exposed. The excavation date is today unless one is entered on the sheet.',
  assessed: 'Every feature must have an assessment result.',
  repaired: 'A repair other than "none" must be recorded.',
  recoated: 'If a feature was assessed for repair or cut-out, a sleeve, wrap or cut-out must be recorded first.',
  backfilled: 'Marks the dig backfilled. The backfill date is today unless one is entered on the sheet. The dig report can then be issued.',
};

// ─── The dig sheet ───────────────────────────────────────────────────────────

function SheetForm({ dig, catalog, meta, onDone }: { dig: PfDigDetail; catalog: PfCatalog | null; meta: PfMeta | null; onDone: () => void }) {
  const u = meta?.units ?? null;
  const update = useUpdatePfDig();
  const [f, setF] = useState({
    chainage: toInput(dig.chainage),
    one_call_ticket: dig.oneCallTicket ?? '',
    one_call_valid_to: dig.oneCallValidTo ?? '',
    excavated_on: dig.excavatedOn ?? '',
    backfilled_on: dig.backfilledOn ?? '',
    coating_type: dig.coatingType ?? '',
    coating_condition: dig.coatingCondition ?? '',
    soil: dig.soil ?? '',
    cp_reading_mv: toInput(dig.cpReadingMv),
    notes: dig.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const chainage = numOrNull(f.chainage);
    const cp = numOrNull(f.cp_reading_mv);
    if ((chainage !== null && !Number.isFinite(chainage)) || (cp !== null && !Number.isFinite(cp))) return setError('Chainage and CP reading must be numbers.');
    const body: PfDigPatch = {
      chainage,
      one_call_ticket: blankToNull(f.one_call_ticket),
      one_call_valid_to: f.one_call_valid_to || null,
      excavated_on: f.excavated_on || null,
      backfilled_on: f.backfilled_on || null,
      coating_type: blankToNull(f.coating_type),
      coating_condition: f.coating_condition || null,
      soil: blankToNull(f.soil),
      cp_reading_mv: cp,
      notes: blankToNull(f.notes),
    };
    update.mutate(
      { id: dig.id, body },
      {
        onSuccess: () => {
          toast.success('Dig sheet saved');
          onDone();
        },
        onError: (err) => setError(pfDigRefusal(err, 'The dig sheet could not be saved.')),
      },
    );
  };
  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="pf-dig-sheet-form">
      <PfFormError message={error} />
      <PfFormGrid>
        <PfField label={unitLabel('Chainage', u?.chainage)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Chainage" value={f.chainage} onChange={set('chainage')} />
        </PfField>
        <PfField label="One-call / GD ticket">
          <input className={PF_INPUT} aria-label="One-call ticket" value={f.one_call_ticket} onChange={set('one_call_ticket')} />
        </PfField>
        <PfField label="Ticket valid to">
          <input type="date" className={PF_DATE_INPUT} aria-label="Ticket valid to" value={f.one_call_valid_to} onChange={set('one_call_valid_to')} />
        </PfField>
        <PfField label="Excavated on">
          <input type="date" className={PF_DATE_INPUT} aria-label="Excavated on" value={f.excavated_on} onChange={set('excavated_on')} />
        </PfField>
        <PfField label="Backfilled on">
          <input type="date" className={PF_DATE_INPUT} aria-label="Backfilled on" value={f.backfilled_on} onChange={set('backfilled_on')} />
        </PfField>
        <PfField label="Coating type">
          <input className={PF_INPUT} aria-label="Coating type" value={f.coating_type} onChange={set('coating_type')} />
        </PfField>
        <PfField label="Coating condition">
          <select className={PF_INPUT} aria-label="Coating condition" value={f.coating_condition} onChange={set('coating_condition')}>
            <option value="">Not recorded</option>
            {(catalog?.coatingConditions ?? []).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Soil">
          <input className={PF_INPUT} aria-label="Soil" value={f.soil} onChange={set('soil')} />
        </PfField>
        <PfField label="CP reading (mV)">
          <input className={PF_INPUT} inputMode="decimal" aria-label="CP reading" value={f.cp_reading_mv} onChange={set('cp_reading_mv')} />
        </PfField>
      </PfFormGrid>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Dig notes" value={f.notes} onChange={set('notes')} />
      </PfField>
      <div className="flex justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onDone} disabled={update.isPending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save sheet'}
        </button>
      </div>
    </form>
  );
}

// ─── Features ────────────────────────────────────────────────────────────────

function FeatureRow({
  feature,
  catalog,
  wtUnit,
  canWork,
}: {
  feature: PfDigFeature;
  catalog: PfCatalog | null;
  wtUnit: string | null | undefined;
  canWork: boolean;
}) {
  const update = useUpdatePfFeature();
  const del = useDeletePfFeature();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <li className="space-y-2 px-4 py-3" data-testid="pf-dig-feature">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono font-bold text-white">
            {feature.anomalyFeatureId || 'Found in ditch'} <span className="font-sans text-sm font-normal text-fieldiq-text">· {feature.featureTypeLabel || feature.featureType}</span>
          </p>
          <p className="mt-1 text-xs text-fieldiq-muted">
            Clock {feature.clockPosition || '—'} · UT min {withUnit(feature.utMinWt, wtUnit)} · Pit {withUnit(feature.pitDepth, wtUnit)} · {fmtNumber(feature.length)} × {fmtNumber(feature.width)}
            {wtUnit ? ` ${wtUnit}` : ''} · MPI {feature.mpiResultLabel || '—'}
            {feature.anomalyDepthPctWt != null ? ` · ILI depth ${fmtNumber(feature.anomalyDepthPctWt)}%` : ''}
          </p>
          {feature.description && <p className="mt-1 text-sm text-fieldiq-text">{feature.description}</p>}
        </div>
        <div className="text-right">
          <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Remaining WT</p>
          <p className="font-mono text-lg font-extrabold text-white" data-testid="pf-feature-remaining">
            {remainingWtText(feature.remainingWtPct, feature.remainingWtReason)}
          </p>
          <p className="text-xs text-fieldiq-muted">{remainingWtBasisLabel(feature.remainingWtBasis)}</p>
        </div>
        <PfBadge skin={pfAssessmentSkin(feature.assessmentResult)} value={feature.assessmentResult ?? 'none'} />
        {canWork && !editing && (
          <div className="flex gap-2">
            <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setEditing(true); }} aria-label={`Edit feature ${feature.anomalyFeatureId ?? ''}`}>
              <Pencil className="size-4" aria-hidden="true" />
            </button>
            <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setDeleting(true); }} aria-label={`Remove feature ${feature.anomalyFeatureId ?? ''}`}>
              <Trash2 className="size-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {deleting && (
        <PfInlineConfirm
          message="Remove this feature from the dig? A feature with a repair recorded against it cannot be removed."
          confirmLabel="Remove feature"
          cancelLabel="Keep it"
          pending={del.isPending}
          error={error}
          onCancel={() => setDeleting(false)}
          onConfirm={() =>
            del.mutate(feature.id, {
              onSuccess: () => {
                toast.success('Feature removed');
                setDeleting(false);
              },
              onError: (err) => setError(pfDigRefusal(err, 'The feature could not be removed.')),
            })
          }
        />
      )}
      {editing && (
        <FeatureForm
          editing
          initial={{
            feature_type: feature.featureType,
            description: feature.description,
            clock_position: feature.clockPosition,
            ut_min_wt: feature.utMinWt,
            pit_depth: feature.pitDepth,
            length: feature.length,
            width: feature.width,
            mpi_result: feature.mpiResult,
            assessment_result: feature.assessmentResult,
            notes: feature.notes,
          }}
          catalog={catalog}
          wtUnit={wtUnit}
          pending={update.isPending}
          error={error}
          submitLabel="Save feature"
          onCancel={() => setEditing(false)}
          onSubmit={(body) => {
            setError(null);
            update.mutate(
              { id: feature.id, body },
              {
                onSuccess: () => {
                  toast.success('Feature saved');
                  setEditing(false);
                },
                onError: (err) => setError(pfDigRefusal(err, 'The feature could not be saved.')),
              },
            );
          }}
        />
      )}
    </li>
  );
}

function FeaturesPanel({ dig, catalog, meta, canWork }: { dig: PfDigDetail; catalog: PfCatalog | null; meta: PfMeta | null; canWork: boolean }) {
  const wtUnit = meta?.units?.wallThickness;
  const add = useAddPfFeature();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const undug = usePfAnomalies({ siteId: dig.siteId, undug: 'true' }, adding);
  return (
    <PfPanel
      eyebrow={`Nominal WT ${withUnit(dig.nominalWt, wtUnit)}`}
      title={`Features (${dig.features.length})`}
      testId="pf-dig-features"
      actions={
        canWork && !adding ? (
          <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setAdding(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Add feature
          </button>
        ) : undefined
      }
    >
      {adding && (
        <div className="border-b border-fieldiq-border px-4 py-4">
          <FeatureForm
            editing={false}
            catalog={catalog}
            wtUnit={wtUnit}
            anomalies={(undug.data?.data ?? []).map((a) => ({
              id: a.id,
              label: `${a.featureId} · ${a.featureTypeLabel ?? a.featureType} · ${withUnit(a.chainage, meta?.units?.chainage)}`,
            }))}
            pending={add.isPending}
            error={error}
            submitLabel="Add feature"
            onCancel={() => setAdding(false)}
            onSubmit={(body) => {
              setError(null);
              add.mutate(
                { digId: dig.id, body },
                {
                  onSuccess: () => {
                    toast.success('Feature added');
                    setAdding(false);
                  },
                  onError: (err) => setError(pfDigRefusal(err, 'The feature could not be added.')),
                },
              );
            }}
          />
        </div>
      )}
      {dig.features.length === 0 ? (
        <PfEmpty>No features recorded yet. Add each feature found once the pipe is exposed.</PfEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {dig.features.map((f) => (
            <FeatureRow key={f.id} feature={f} catalog={catalog} wtUnit={wtUnit} canWork={canWork} />
          ))}
        </ul>
      )}
      {dig.nominalWt == null && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
          No nominal wall thickness on the site or segment, so remaining WT cannot be worked out. Set it on the site.
        </p>
      )}
    </PfPanel>
  );
}

// ─── Repairs ─────────────────────────────────────────────────────────────────

function RepairForm({
  dig,
  catalog,
  repair,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  dig: PfDigDetail;
  catalog: PfCatalog | null;
  repair?: PfDigRepair;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: { method?: string; feature_id?: string | null; material?: string | null; repaired_on?: string | null; notes?: string | null }) => void;
}) {
  const [method, setMethod] = useState(repair?.method ?? '');
  const [featureId, setFeatureId] = useState(repair?.featureId ?? '');
  const [material, setMaterial] = useState(repair?.material ?? '');
  const [on, setOn] = useState(repair?.repairedOn ?? '');
  const [notes, setNotes] = useState(repair?.notes ?? '');
  const [local, setLocal] = useState<string | null>(null);
  return (
    <form
      className="space-y-3"
      data-testid="pf-repair-form"
      onSubmit={(e) => {
        e.preventDefault();
        setLocal(null);
        if (!method) return setLocal('Choose the repair method.');
        onSubmit({ method, feature_id: featureId || null, material: blankToNull(material), repaired_on: on || null, notes: blankToNull(notes) });
      }}
    >
      <PfFormError message={local ?? error} />
      <PfFormGrid>
        <PfField label="Method">
          <select className={PF_INPUT} aria-label="Repair method" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Choose</option>
            {(catalog?.repairMethods ?? []).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Feature">
          <select className={PF_INPUT} aria-label="Repaired feature" value={featureId} onChange={(e) => setFeatureId(e.target.value)}>
            <option value="">The dig as a whole</option>
            {dig.features.map((f) => (
              <option key={f.id} value={f.id}>
                {f.anomalyFeatureId || 'Found in ditch'} · {f.featureTypeLabel || f.featureType}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Material">
          <input className={PF_INPUT} aria-label="Repair material" value={material} onChange={(e) => setMaterial(e.target.value)} />
        </PfField>
        <PfField label="Repaired on">
          <input type="date" className={PF_DATE_INPUT} aria-label="Repaired on" value={on} onChange={(e) => setOn(e.target.value)} />
        </PfField>
      </PfFormGrid>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-16 py-2`} aria-label="Repair notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </PfField>
      <div className="flex justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : repair ? 'Save repair' : 'Record repair'}
        </button>
      </div>
    </form>
  );
}

function RepairRow({
  dig,
  repair,
  catalog,
  meta,
  canWork,
  canDispatch,
}: {
  dig: PfDigDetail;
  repair: PfDigRepair;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  canWork: boolean;
  canDispatch: boolean;
}) {
  const del = useDeletePfRepair();
  const unlinkPt = useUnlinkPfPt();
  const [deleting, setDeleting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feature = dig.features.find((f) => f.id === repair.featureId);
  return (
    <li className="space-y-3 px-4 py-3" data-testid="pf-dig-repair">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-white">{repair.methodLabel || catalogLabel(catalog?.repairMethods, repair.method)}</p>
          <p className="mt-1 text-xs text-fieldiq-muted">
            {feature ? `${feature.anomalyFeatureId || 'Found in ditch'} · ` : ''}
            {repair.material || '—'} · {pfDay(repair.repairedOn)}
          </p>
          {repair.notes && <p className="mt-1 text-sm text-fieldiq-text">{repair.notes}</p>}
        </div>
        {canWork && (
          <div className="flex flex-wrap gap-2">
            <Link className={PF_SECONDARY} to={`/pipeline-facility/jobs/${dig.jobId}/welds/new?digRepairId=${repair.id}`}>
              <Plus className="size-4" aria-hidden="true" />
              Weld
            </Link>
            {canDispatch && (
              <button type="button" className={PF_SECONDARY} onClick={() => setPicking(true)}>
                Link pressure test
              </button>
            )}
            <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setDeleting(true); }} aria-label="Remove repair">
              <Trash2 className="size-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      <PfFormError message={!deleting ? error : null} />
      {deleting && (
        <PfInlineConfirm
          message="Remove this repair? A repair with welds or pressure tests linked to it cannot be removed."
          confirmLabel="Remove repair"
          cancelLabel="Keep it"
          pending={del.isPending}
          error={error}
          onCancel={() => setDeleting(false)}
          onConfirm={() =>
            del.mutate(repair.id, {
              onSuccess: () => {
                toast.success('Repair removed');
                setDeleting(false);
              },
              onError: (err) => setError(pfDigRefusal(err, 'The repair could not be removed.')),
            })
          }
        />
      )}
      {(repair.welds.length > 0 || repair.ptLinks.length > 0) && (
        <div className="grid gap-2 sm:grid-cols-2">
          {repair.welds.map((w) => (
            <Link
              key={w.id}
              to={`/pipeline-facility/welds/${w.id}`}
              className="flex items-center gap-2 rounded-fi-control border border-fieldiq-border px-3 py-2 text-sm hover:border-fi-amber"
              data-testid="pf-repair-weld"
            >
              <Hammer className="size-4 text-fieldiq-muted" aria-hidden="true" />
              <span className="font-mono font-bold text-white">{w.weldNumber}</span>
              <span className="text-fieldiq-text">{w.welderName || '—'}</span>
              <span className="ml-auto">
                <PfBadge skin={pfWeldResultSkin(w.ndtResult)} value={w.ndtResult} />
              </span>
            </Link>
          ))}
          {repair.ptLinks.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-fi-control border border-fieldiq-border px-3 py-2 text-sm" data-testid="pf-repair-pt">
              <span className="font-mono font-bold text-white">{p.testMissing ? 'Test removed' : p.ptJobNumber || '—'}</span>
              <span className="text-fieldiq-text">
                {p.testTypeName || '—'} · {withUnit(p.testPressure, meta?.units?.pressure)} · {p.result || 'No result'}
              </span>
              {canDispatch && (
                <button
                  type="button"
                  className="ml-auto text-xs font-bold text-fieldiq-muted underline"
                  onClick={() =>
                    unlinkPt.mutate(p.id, {
                      onSuccess: () => toast.success('Pressure test unlinked'),
                      onError: (err) => setError(pfDigRefusal(err, 'The pressure test could not be unlinked.')),
                    })
                  }
                >
                  Unlink
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {picking && (
        <PtLinkPicker digRepairId={repair.id} pressureUnit={meta?.units?.pressure} timeZone={meta?.timeZone} onDone={() => setPicking(false)} />
      )}
    </li>
  );
}

function RepairsPanel({
  dig,
  catalog,
  meta,
  canWork,
  canDispatch,
}: {
  dig: PfDigDetail;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  canWork: boolean;
  canDispatch: boolean;
}) {
  const add = useAddPfRepair();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <PfPanel
      eyebrow="Repairs, welds and pressure tests"
      title={`Repairs (${dig.repairs.length})`}
      testId="pf-dig-repairs"
      actions={
        canWork && !adding ? (
          <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setAdding(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Record repair
          </button>
        ) : undefined
      }
    >
      {adding && (
        <div className="border-b border-fieldiq-border px-4 py-4">
          <RepairForm
            dig={dig}
            catalog={catalog}
            pending={add.isPending}
            error={error}
            onCancel={() => setAdding(false)}
            onSubmit={(body) => {
              setError(null);
              add.mutate(
                { digId: dig.id, body },
                {
                  onSuccess: () => {
                    toast.success('Repair recorded');
                    setAdding(false);
                  },
                  onError: (err) => setError(pfDigRefusal(err, 'The repair could not be recorded.')),
                },
              );
            }}
          />
        </div>
      )}
      {dig.repairs.length === 0 ? (
        <PfEmpty>No repairs recorded. A feature assessed for repair or cut-out needs one before the dig is recoated.</PfEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {dig.repairs.map((r) => (
            <RepairRow key={r.id} dig={dig} repair={r} catalog={catalog} meta={meta} canWork={canWork} canDispatch={canDispatch} />
          ))}
        </ul>
      )}
    </PfPanel>
  );
}

// ─── Report card ─────────────────────────────────────────────────────────────

function ReportCard({ dig, meta, canDispatch }: { dig: PfDigDetail; meta: PfMeta | null; canDispatch: boolean }) {
  const create = useCreatePfReport();
  const reportQuery = usePfReport(dig.reportId ?? undefined);
  const [error, setError] = useState<string | null>(null);
  const report = reportQuery.data?.data ?? null;
  return (
    <PfPanel eyebrow="Dig report" title={dig.reportNumber || (dig.reportId ? 'Draft report' : 'No report yet')} testId="pf-dig-report">
      {!dig.reportId ? (
        <div className="space-y-3 px-4 py-4">
          <PfFormError message={error} />
          <p className="text-sm text-fieldiq-text">
            The dig report gathers the sheet, the features and the repairs. Draft it at any time; it is issued once the dig is backfilled.
          </p>
          {canDispatch && (
            <button
              type="button"
              className={PF_PRIMARY}
              disabled={create.isPending}
              data-testid="pf-report-create"
              onClick={() => {
                setError(null);
                create.mutate(
                  { digId: dig.id, body: {} },
                  {
                    onSuccess: () => toast.success('Draft report created'),
                    onError: (err) => setError(pfDigRefusal(err, 'The draft could not be created.')),
                  },
                );
              }}
            >
              {create.isPending ? 'Creating…' : 'Create draft report'}
            </button>
          )}
        </div>
      ) : reportQuery.isLoading ? (
        <PfLoading label="Loading report" />
      ) : !report ? (
        <div className="px-4 py-4">
          <LoadFailureNotice error={queryFailureError(reportQuery)} kind={queryLoadFailure(reportQuery) ?? 'unreachable'} subject="the dig report" />
        </div>
      ) : (
        <ReportPanelBody report={report} canWrite={canDispatch} showOpenLink timeZone={meta?.timeZone} />
      )}
    </PfPanel>
  );
}

// ─── The page ────────────────────────────────────────────────────────────────

export default function PfDigDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/digs');
  const { user } = useAuth();
  const canDispatch = canDispatchPf(user?.role);
  const query = usePfDig(id);
  const catalog = usePfMeta().data?.data ?? null;
  const update = useUpdatePfDig();
  const del = useDeletePfDig();
  const [move, setMove] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dig = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const u = meta?.units ?? null;
  const failure = queryLoadFailure(query);
  const editable = !!dig && pfDigEditable(dig.status, dig.jobStatus);
  const canWork = canWorkPf(user?.role) && editable;
  const steps = canWork ? pfDigSteps(dig?.allowedNext) : [];

  const doMove = () => {
    if (!dig || !move) return;
    setMoveError(null);
    update.mutate(
      { id: dig.id, body: { status: move } },
      {
        onSuccess: () => {
          toast.success(`${dig.digNumber} ${pfDigStatusSkin(move).label.toLowerCase()}`);
          setMove(null);
        },
        onError: (err) => setMoveError(pfDigRefusal(err, 'The status could not be changed.')),
      },
    );
  };

  return (
    <div data-testid="pf-dig-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={dig ? `${PF_EYEBROW} · ${dig.jobNumber ?? 'Dig'}` : PF_EYEBROW}
        title={dig?.digNumber || (query.isLoading ? 'Dig' : '—')}
        description={dig ? `${dig.siteName ?? '—'} · Chainage ${withUnit(dig.chainage, u?.chainage)}${dig.segmentLabel ? ` · ${dig.segmentLabel}` : ''}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          dig ? (
            <div className="flex flex-wrap gap-2" data-testid="pf-dig-actions">
              {steps.map((to, i) => (
                <button
                  key={to}
                  type="button"
                  data-testid={`pf-dig-step-${to}`}
                  className={i === 0 ? 'fi-action fi-action--standard fi-action--primary' : DH_BAND_SECONDARY}
                  onClick={() => { setMoveError(null); setMove(to); }}
                >
                  {PF_DIG_MOVE_LABEL[to] ?? pfDigStatusSkin(to).label}
                </button>
              ))}
              {canWork && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => setEditing(true)} disabled={editing}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit sheet
                </button>
              )}
              {canDispatch && dig.status === 'planned' && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: dig ? pfDigStatusSkin(dig.status).label : '—' },
          { icon: <Layers className="size-4" aria-hidden="true" />, label: 'Features', tone: 'neutral', loading: query.isLoading, value: dig ? displayValue(dig.features.length) : '—' },
          { icon: <Hammer className="size-4" aria-hidden="true" />, label: 'Repairs', tone: 'neutral', loading: query.isLoading, value: dig ? displayValue(dig.repairs.length) : '—' },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading dig" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !dig) ? (
            <PfPanel eyebrow="Dig" title="Dig">
              <PfEmpty>This dig was not found. It may have been deleted, or you may not be on its job&apos;s crew.</PfEmpty>
            </PfPanel>
          ) : failure || !dig ? (
            <PfPanel eyebrow="Dig" title="Dig">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this dig" />
            </PfPanel>
          ) : (
            <>
              {move && (
                <PfInlineConfirm
                  testId="pf-dig-move-confirm"
                  message={MOVE_BODY[move] ?? 'Change the dig status?'}
                  confirmLabel={PF_DIG_MOVE_LABEL[move] ?? 'Confirm'}
                  pending={update.isPending}
                  error={moveError}
                  onCancel={() => { setMove(null); setMoveError(null); }}
                  onConfirm={doMove}
                />
              )}
              {deleting && (
                <PfInlineConfirm
                  testId="pf-dig-delete-confirm"
                  message={`Delete ${dig.digNumber}? Its features go with it and its anomalies return to the undug list. Only a planned dig can be deleted.`}
                  confirmLabel="Delete dig"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(dig.id, {
                      onSuccess: () => {
                        toast.success(`${dig.digNumber} deleted`);
                        navigate(`/pipeline-facility/jobs/${dig.jobId}?tab=digs`, { replace: true });
                      },
                      onError: (err) => setDeleteError(pfDigRefusal(err, 'The dig could not be deleted.')),
                    })
                  }
                />
              )}
              {pfDigAwaitsReport(dig.status) && (
                <PfNote testId="pf-dig-awaits-report">Backfilled. The dig is marked reported when its dig report is issued — see the report card below.</PfNote>
              )}
              {dig.status === 'reported' && <PfNote testId="pf-dig-final">This dig is reported. Its sheet, features and repairs are final.</PfNote>}
              {(dig.jobStatus === 'completed' || dig.jobStatus === 'cancelled') && dig.status !== 'reported' && (
                <PfNote>The job is {dig.jobStatus}, so this dig can no longer be changed.</PfNote>
              )}

              <PfPanel eyebrow="Dig sheet" title="Location, permits and pipe" testId="pf-dig-sheet">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="Status" value={<PfBadge skin={pfDigStatusSkin(dig.status)} value={dig.status} />} />
                  <PfSpec label="Job" value={<Link className="underline" to={`/pipeline-facility/jobs/${dig.jobId}?tab=digs`}>{dig.jobNumber ?? '—'}</Link>} />
                  <PfSpec label="Customer" value={dig.customerName || '—'} />
                  <PfSpec label="Site" value={<Link className="underline" to={`/pipeline-facility/sites/${dig.siteId}`}>{dig.siteName ?? '—'}</Link>} />
                  <PfSpec label="Segment" value={dig.segmentLabel || '—'} />
                  <PfSpec label="Chainage" value={withUnit(dig.chainage, u?.chainage)} mono />
                  <PfSpec label="One-call / GD ticket" value={<span data-testid="pf-dig-onecall">{oneCallText(dig)}</span>} />
                  <PfSpec label="Nominal WT" value={withUnit(dig.nominalWt, u?.wallThickness)} mono />
                  <PfSpec label="Excavated" value={pfDay(dig.excavatedOn)} />
                  <PfSpec label="Backfilled" value={pfDay(dig.backfilledOn)} />
                  <PfSpec label="Coating" value={coatingText(dig.coatingType, dig.coatingConditionLabel)} />
                  <PfSpec label="Soil" value={dig.soil || '—'} />
                  <PfSpec label="CP reading" value={dig.cpReadingMv == null ? '—' : `${fmtNumber(dig.cpReadingMv)} mV`} mono />
                  <PfSpec label="Latitude" value={fmtNumber(dig.latitude)} mono />
                  <PfSpec label="Longitude" value={fmtNumber(dig.longitude)} mono />
                </dl>
                {dig.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{dig.notes}</p>}
                {editing && canWork && (
                  <div className="border-t border-fieldiq-border">
                    <SheetForm dig={dig} catalog={catalog} meta={meta} onDone={() => setEditing(false)} />
                  </div>
                )}
              </PfPanel>

              <FeaturesPanel dig={dig} catalog={catalog} meta={meta} canWork={canWork} />
              <RepairsPanel dig={dig} catalog={catalog} meta={meta} canWork={canWork} canDispatch={canDispatch && editable} />
              <ReportCard dig={dig} meta={meta} canDispatch={canDispatch} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
